import React, { Fragment } from 'react';
import { useIntl } from 'react-intl';
import { Col, Row } from 'reactstrap';

import {
  EXPORTS, IMPORTS,
  SectionContainer, SectionHeader, SectionWrapper,
  UNIT_CARBON, UNIT_CARBON_DEFAULT_LABEL,
  UNIT_CURRENCY,
  UNIT_ENERGY, UNIT_ENERGY_LABEL,
} from 'src/enosikit/components/Chart/components/chartConstants';
import {
  extractTradeValues, getDirectionLabel, getMeterChartToolipLabel,
  getTooltipFormatFunc,
} from 'src/enosikit/components/Chart/helpers/tooltip';
import {
  BUY, DATA_AGGREGATE_BY_METER, DATA_AGGREGATE_BY_PROPERTY,
  DATA_GROUP_BY_COUNTERPARTY, DATA_GROUP_BY_TRADE_TYPE, SELL,
} from 'src/util/constants';
import { getTradeTypeLabel } from 'src/util/i18n/constants';

/**
 * Renders the tooltip content for trade chart
 * @param {object} data - the tooltip datapack.
 * @param {string} unitLabel
 * @param {object} chartView - { aggregateBy, groupBy }
 * @param {DATA_AGGREGATE_BY_PROPERTY | DATA_AGGREGATE_BY_METER} chartView.aggregateBy
 * @param {DATA_GROUP_BY_COUNTERPARTY | DATA_GROUP_BY_TRADE_TYPE} chartView.groupBy
 * @param {UNIT_CURRENCY | UNIT_ENERGY | UNIT_CARBON} unit - chart unit
 * @returns {React.ReactElement | null} - trade tooltip.
 */
export const tradeChartTooltip = (
  data,
  unitLabel,
  chartView,
  unit,
) => {
  if (!data || !chartView) {
    return null;
  }
  const intl = useIntl();
  const values = extractTradeValues(data);
  const tooltipFormatFunc = getTooltipFormatFunc(unit, values);

  const tradeTooltip = [BUY, SELL].map((direction) => {
    if (!data[direction] || Object.keys(data[direction]).length === 0) {
      return null;
    }

    const tooltipLabel = direction === BUY
      ? getDirectionLabel(IMPORTS, intl) : getDirectionLabel(EXPORTS, intl);
    const { aggregateBy } = chartView;

    const aggByProperty = aggregateBy === DATA_AGGREGATE_BY_PROPERTY;

    return (
      <SectionWrapper key={direction}>
        {!aggByProperty && (
          <SectionHeader>
            {tooltipLabel}
          </SectionHeader>
        )}

        {Object.keys(data[direction]).map((key) => {
          const d = data[direction][key];
          if (!d) {
            return null;
          }
          const {
            label, property, subLabel, value,
          } = d;
          const tradeTypeLookupKey = aggByProperty ? subLabel : label;
          const tradeTypeLabel = getTradeTypeLabel(intl, tradeTypeLookupKey);

          return (
            <Fragment key={key}>
              {(aggByProperty && property?.title) && (
                <SectionHeader>
                  {`${tooltipLabel} - ${property.title}`}
                </SectionHeader>
              )}
              <Row key={key} className="mb-1">
                <Col className="pe-0">
                  <span>{tradeTypeLabel}</span>
                </Col>
                <Col xs="auto">
                  {tooltipFormatFunc(value, unitLabel)}
                </Col>
              </Row>

            </Fragment>
          );
        })}
      </SectionWrapper>
    );
  });
  return tradeTooltip;
};

/**
 * Forms the content for the meter tooltip
 * @param {object} tooltipData
 * @param {string} dirLabel
 * @param {string} unitLabel
 * @param {Function} tooltipFormatFunc
 * @returns {React.ReactElement} - meter tooltip content.
 */
const renderMeterViewContent = (
  tooltipData,
  dirLabel,
  unitLabel,
  tooltipFormatFunc,
) => {
  const {
    flags, value, title, identifier,
  } = tooltipData || {};

  return (
    <SectionWrapper>
      <SectionHeader>
        {getMeterChartToolipLabel(dirLabel, { title, identifier })}
      </SectionHeader>
      <div>
        {tooltipFormatFunc(value, unitLabel)}
      </div>
      {flags?.length > 0 && (
        <SectionContainer>
          {flags.map((el) => el.identifier).join(', ')}
        </SectionContainer>
      )}
    </SectionWrapper>
  );
};

/**
 * Renders the meter tooltip
 * @param {object} tooltipData
 * @param {string} dirLabel
 * @param {string} unitLabel
 * @param {boolean} aggByProperty
 * @param {Function} tooltipFormatFunc
 * @returns {React.ReactElement} - meter tooltip content.
 */

const getMeterTooltipData = (
  tooltipData,
  dirLabel,
  unitLabel,
  aggByProperty,
  tooltipFormatFunc,
) => {
  if (!aggByProperty) {
    return renderMeterViewContent(
      tooltipData,
      dirLabel,
      unitLabel,
      tooltipFormatFunc,

    );
  }

  const meterTooltipData = Object.keys(tooltipData).map((meterId) => (
    <Fragment key={meterId}>
      {renderMeterViewContent(
        tooltipData[meterId],
        dirLabel,
        unitLabel,
        tooltipFormatFunc,

      )}
    </Fragment>
  ));
  return meterTooltipData;
};

/**
 * Renders the tooltip content for a meter chart.
 * @param {object} tooltipData - The data to be displayed in the tooltip.
 * @param {UNIT_CARBON_DEFAULT_LABEL | UNIT_ENERGY_LABEL | string} unitLabel - The label for
 * the unit of the data.
 * @param {object} chartView - { aggregateBy, groupBy }
 * @param {DATA_AGGREGATE_BY_PROPERTY | DATA_AGGREGATE_BY_METER} chartView.aggregateBy
 * @param {DATA_GROUP_BY_COUNTERPARTY | DATA_GROUP_BY_TRADE_TYPE} chartView.groupBy
 * @param {UNIT_CURRENCY | UNIT_CARBON |  UNIT_ENERGY} unit
 * @returns {React.ReactElement | null} - The rendered tooltip content,
 */
export const meterChartTooltip = (
  tooltipData,
  unitLabel,
  chartView,
  unit,
) => {
  if (!tooltipData || !chartView) {
    return null;
  }
  const { tooltipConsumed, tooltipGenerated } = tooltipData;
  if (!tooltipConsumed && !tooltipGenerated) {
    return null;
  }
  const intl = useIntl();
  const { aggregateBy } = chartView;
  const aggByProperty = aggregateBy === DATA_AGGREGATE_BY_PROPERTY;
  const tooltipFormatFunc = getTooltipFormatFunc(unit);

  const tooltips = [
    {
      key: 'tooltip-consumed',
      tooltip: (tooltipConsumed
        && (getMeterTooltipData(
          tooltipConsumed,
          getDirectionLabel(IMPORTS, intl),
          unitLabel,
          aggByProperty,
          tooltipFormatFunc,

        ))
      ),
    }, {
      key: 'tooltip-generated',
      tooltip: (tooltipGenerated
        && (getMeterTooltipData(
          tooltipGenerated,
          getDirectionLabel(EXPORTS, intl),
          unitLabel,
          aggByProperty,
          tooltipFormatFunc,
        ))),
    },
  ];

  return (
    tooltips.map((tt) => (
      <Fragment key={tt.key}>
        {tt.tooltip}
      </Fragment>
    ))
  );
};
